import {
  AccidentCircumstanceKeyEnum,
  AuthorityType,
  FormikAccidentCircumstances,
  FormikAccidentData,
  FormikAccidentGeneralInfo,
  FormikAccidentMaterialDamages,
  FormikInjured,
  FormikInjuredsInfo,
  FormikWitness,
  FormikWitnessesInfo,
} from "src/types/formik/formik-accident.types";
import {
  CircostanzeIncidenteDTO,
  DatiGeneraliDTO,
  FeritiDTO,
  TestimoniDTO,
} from "../swagger/swagger-accident.types";
import { DateMapper, DateTimeMapper, YesNoAnswerMapper } from "./mappers-utils";
import { Optional, YesNoAnswer } from "src/types/commons.types";
import { deleteEmptyKeys } from "src/utils";

export const AccidentMapper = {
  fromDTO(dto: DatiGeneraliDTO): FormikAccidentData {
    const { date, time } = DateTimeMapper.fromDto(dto.dataIncidente);

    return {
      generalInfo: GeneralInfoMapper.fromDTO({
        accidentDateDto: date ?? "",
        accidentHourDto: time ?? "",
        accidentLocationDto: dto.luogoIncidente,
      }),
      injuredsInfo: InjuredsInfoMapper.fromDTO({
        injuredQuestionDto: dto.feriti,
        injuredListDto: dto.listaFeriti,
      }),
      materialDamages: MaterialDamagesMapper.fromDTO({
        otherVehiclesDamagedDto: dto.danniAltriVeicoli,
        otherVehiclesDamageDetailsDto: dto.ulterioriDatiDanniVeicoli,
        otherObjectsDamagedDto: dto.danniAltriOggetti,
        otherObjectsDamageDetailsDto: dto.ulterioriDatiDanniOggetti,
      }),
      witnessesInfo: WitnessesInfoMapper.fromDTO({
        authorityDto: dto.autorita,
        authorityInterventionDto: dto.interventoAutorita,
        witnessesListDto: dto.testimoni,
      }),
      vehicleAccidentCircumstances: dto.circostanzeIncidenteVeicolo.map(
        CircumstancesMapper.fromDTO,
      ),
    };
  },

  toDTO(form: FormikAccidentData): DatiGeneraliDTO {
    return deleteEmptyKeys({
      dataIncidente: DateTimeMapper.toDto(
        form.generalInfo.accidentDate,
        form.generalInfo.accidentHour,
      ),
      luogoIncidente: form.generalInfo.accidentLocation,
      feriti: YesNoAnswerMapper.toDto(
        form.injuredsInfo.injuredQuestion ?? YesNoAnswer.NO,
      ),
      listaFeriti: form.injuredsInfo.injuredList.map(InjuredMapper.toDTO),
      interventoAutorita: YesNoAnswerMapper.toDto(
        form.witnessesInfo.authorityIntervention,
      ),
      autorita: form.witnessesInfo.authority ?? AuthorityType.EMPTY,
      danniAltriVeicoli: YesNoAnswerMapper.toDto(
        form.materialDamages.otherVehiclesDamaged ?? YesNoAnswer.NO,
      ),
      ulterioriDatiDanniVeicoli:
        form.materialDamages.otherVehiclesDamageDetails,
      danniAltriOggetti: YesNoAnswerMapper.toDto(
        form.materialDamages.otherObjectsDamaged ?? YesNoAnswer.NO,
      ),
      ulterioriDatiDanniOggetti: form.materialDamages.otherObjectsDamageDetails,
      testimoni: form.witnessesInfo.witnessesList.map(WitnessMapper.toDTO),
      circostanzeIncidenteVeicolo: form.vehicleAccidentCircumstances.map(
        CircumstancesMapper.toDTO,
      ),
    });
  },
};

export const GeneralInfoMapper = {
  fromDTO({
    accidentDateDto,
    accidentLocationDto,
    accidentHourDto,
  }: {
    accidentDateDto: string;
    accidentLocationDto: string;
    accidentHourDto: string;
  }): FormikAccidentGeneralInfo {
    return {
      accidentDate: accidentDateDto,
      accidentHour: accidentHourDto,
      accidentLocation: accidentLocationDto,
    };
  },
};

export const InjuredsInfoMapper = {
  fromDTO({
    injuredQuestionDto,
    injuredListDto,
  }: {
    injuredQuestionDto?: boolean;
    injuredListDto?: FeritiDTO[];
  }): FormikInjuredsInfo {
    return {
      injuredQuestion: YesNoAnswerMapper.fromDto(injuredQuestionDto),
      injuredList: injuredListDto?.map(InjuredMapper.fromDTO) ?? [],
    };
  },
};

export const MaterialDamagesMapper = {
  fromDTO({
    otherVehiclesDamagedDto,
    otherVehiclesDamageDetailsDto,
    otherObjectsDamagedDto,
    otherObjectsDamageDetailsDto,
  }: {
    otherVehiclesDamagedDto?: boolean;
    otherVehiclesDamageDetailsDto?: string;
    otherObjectsDamagedDto?: boolean;
    otherObjectsDamageDetailsDto?: string;
  }): FormikAccidentMaterialDamages {
    return {
      otherVehiclesDamaged: YesNoAnswerMapper.fromDto(otherVehiclesDamagedDto),
      otherVehiclesDamageDetails: otherVehiclesDamageDetailsDto ?? "",
      otherObjectsDamaged: YesNoAnswerMapper.fromDto(otherObjectsDamagedDto),
      otherObjectsDamageDetails: otherObjectsDamageDetailsDto ?? "",
    };
  },
};

export const WitnessesInfoMapper = {
  fromDTO({
    authorityInterventionDto,
    authorityDto,
    witnessesListDto,
  }: {
    authorityInterventionDto?: boolean;
    authorityDto?: AuthorityType;
    witnessesListDto?: TestimoniDTO[];
  }): FormikWitnessesInfo {
    return {
      witnessesList: witnessesListDto?.map(WitnessMapper.fromDTO) ?? [],
      authorityIntervention: YesNoAnswerMapper.fromDto(
        authorityInterventionDto,
      ),
      authority: authorityDto,
    };
  },
};

export const InjuredMapper = {
  fromDTO(dto: FeritiDTO): FormikInjured {
    return {
      type: dto.tipo!,
      surname: dto.cognome ?? "",
      name: dto.nome ?? "",
      phoneNumber: {
        formattedPhone: dto.telefono ?? "",
      },
      email: dto.email ?? "",
      fiscalCode: dto.codiceFiscale ?? "",
      address: dto.indirizzo ?? "",
      placeOfBirth: dto.luogoNascita ?? "",
      dateOfBirth: DateMapper.fromDto(dto.dataNascita ?? ""),
      emergencyRoom: YesNoAnswerMapper.fromDto(dto.prontoSoccorso),
      emergencyRoomName: dto.nomeProntoSoccorso ?? "",
      emergencyRoomMunicipality: dto.comuneProntoSoccorso ?? "",
    };
  },

  toDTO(form: FormikInjured): FeritiDTO {
    return deleteEmptyKeys({
      tipo: form.type,
      cognome: form.surname,
      nome: form.name,
      telefono: form.phoneNumber.formattedPhone ?? "",
      email: form.email,
      codiceFiscale: form.fiscalCode,
      indirizzo: form.address,
      luogoNascita: form.placeOfBirth,
      dataNascita: DateMapper.toDto(form.dateOfBirth),
      prontoSoccorso: YesNoAnswerMapper.toDto(form.emergencyRoom),
      nomeProntoSoccorso: form.emergencyRoomName,
      comuneProntoSoccorso: form.emergencyRoomMunicipality,
    });
  },
};

export const WitnessMapper = {
  fromDTO(dto: TestimoniDTO): FormikWitness {
    return {
      name: dto.nome,
      surname: dto.cognome,
      address: dto.indirizzo,
      fiscalCode: dto.codiceFiscale ?? "",
      email: dto.email,
      phoneNumber: {
        formattedPhone: dto.telefono ?? "",
      },
      placeOfBirth: dto.luogoNascita,
      dateOfBirth: DateMapper.fromDto(dto.dataNascita),
    };
  },

  toDTO(form: FormikWitness): TestimoniDTO {
    return deleteEmptyKeys({
      nome: form.name,
      cognome: form.surname,
      indirizzo: form.address,
      codiceFiscale: form.fiscalCode,
      email: form.email,
      telefono: form.phoneNumber.formattedPhone ?? "",
      luogoNascita: form.placeOfBirth,
      dataNascita: DateMapper.toDto(form.dateOfBirth),
    });
  },
};

export const CircumstancesMapper = {
  fromDTO(dto: CircostanzeIncidenteDTO): FormikAccidentCircumstances {
    const activeCircumstanceKey = Object.entries(dto).find(
      ([_, value]) => value === true,
    )?.[0];

    const selectedCircumstance: Optional<AccidentCircumstanceKeyEnum> =
      activeCircumstanceKey
        ? (Object.entries(AccidentCircumstanceKeyEnum).find(
            ([, backendKey]) => backendKey === activeCircumstanceKey,
          )?.[1] as AccidentCircumstanceKeyEnum)
        : undefined;

    return {
      selectedCircumstance: selectedCircumstance,
      vehicleType: dto.tipoVeicolo,
    };
  },

  toDTO(form: FormikAccidentCircumstances): CircostanzeIncidenteDTO {
    const base: CircostanzeIncidenteDTO = {
      inFermataOSosta: false,
      ripartivaDopoSosta: false,
      stavaParcheggiando: false,
      entravaInUnParcheggio: false,
      uscivaDaParcheggio: false,
      siImmettevaInUnaPiazza: false,
      circolavaSuUnaPiazza: false,
      tamponavaStessoSenso: false,
      procedevaStessoSenso: false,
      cambiavaFila: false,
      sorpassava: false,
      giravaADestra: false,
      giravaASinistra: false,
      retrocedeva: false,
      invadeSede: false,
      provieneDestra: false,
      noPrecedenzaOSemaforoRosso: false,
      tipoVeicolo: form.vehicleType,
    };

    if (!form.selectedCircumstance) {
      return base;
    }

    return deleteEmptyKeys({
      ...base,
      [form.selectedCircumstance]: true,
    } as CircostanzeIncidenteDTO);
  },
};
